import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_BASE_URL } from './api-base';

export interface ControlProject {
  id: number;
  tenant_id: number;
  code?: string | null;
  name: string;
  description?: string | null;
  status: string;
  start_date?: string | null;
  end_date?: string | null;
  manager_user_id?: number | null;
  manager_name?: string | null;
  department_id?: number | null;
  department_name?: string | null;
  created_by: number;
  created_at: string;
  updated_at?: string | null;
  members_count?: number;
}

export interface ControlProjectTapVersion {
  id: number;
  project_id: number;
  tenant_id: number;
  version_number: number;
  title?: string | null;
  justification?: string | null;
  objectives?: string | null;
  business_scope?: string | null;
  technical_scope?: string | null;
  deliverables?: string | null;
  non_deliverables?: string | null;
  permissions?: string | null;
  constraints?: string | null;
  assumptions?: string | null;
  risks?: string | null;
  success_criteria?: string | null;
  budget?: string | null;
  created_by: number;
  created_at: string;
}

export interface ControlProjectMember {
  id: number;
  project_id: number;
  tenant_id: number;
  user_id: number;
  user_name?: string | null;
  role: 'MANAGER' | 'MEMBER' | 'OBSERVER';
  created_at: string;
}

export interface ControlProjectMilestone {
  id: number;
  project_id: number;
  tenant_id: number;
  title: string;
  description?: string | null;
  due_date: string;
  status: 'PLANNED' | 'DONE' | 'LATE';
  created_by: number;
  created_at: string;
}

export interface ControlProjectAttachment {
  id: number;
  project_id: number;
  tenant_id: number;
  file_name: string;
  file_path: string;
  file_type?: string | null;
  file_size?: number;
  uploaded_by: number;
  uploaded_at: string;
  file_url: string;
  is_image?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ControlprojectService {
  private readonly baseUrl = `${API_BASE_URL}/controlproject`;

  constructor(private http: HttpClient) {}

  getProjects(tenantId: number, filters: Record<string, any> = {}): Observable<ControlProject[]> {
    let params = new HttpParams().set('tenant_id', tenantId);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, value);
      }
    });
    return this.http.get<ControlProject[]>(`${this.baseUrl}/projects`, { params });
  }

  getActiveProjectsCount(tenantId: number, userId: number): Observable<{ count: number }> {
    const params = new HttpParams()
      .set('tenant_id', tenantId)
      .set('user_id', userId);
    return this.http.get<{ count: number }>(`${this.baseUrl}/projects/active-count`, { params });
  }

  getActiveProjectsList(
    tenantId: number,
    userId: number,
    limit = 10
  ): Observable<Array<{ id: number; code?: string | null; name: string; status: string; created_at: string }>> {
    const params = new HttpParams()
      .set('tenant_id', tenantId)
      .set('user_id', userId)
      .set('limit', limit);
    return this.http.get<Array<{ id: number; code?: string | null; name: string; status: string; created_at: string }>>(
      `${this.baseUrl}/projects/active-list`,
      { params }
    );
  }

  createProject(payload: any): Observable<ControlProject> {
    return this.http.post<ControlProject>(`${this.baseUrl}/projects`, payload);
  }

  getProject(tenantId: number, projectId: number): Observable<ControlProject> {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<ControlProject>(`${this.baseUrl}/projects/${projectId}`, { params });
  }

  updateProject(projectId: number, payload: any): Observable<ControlProject> {
    return this.http.put<ControlProject>(`${this.baseUrl}/projects/${projectId}`, payload);
  }

  deleteProject(tenantId: number, projectId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.delete(`${this.baseUrl}/projects/${projectId}`, { params });
  }

  getMembers(tenantId: number, projectId: number): Observable<ControlProjectMember[]> {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<ControlProjectMember[]>(`${this.baseUrl}/projects/${projectId}/members`, { params });
  }

  addMember(projectId: number, payload: any): Observable<ControlProjectMember[]> {
    return this.http.post<ControlProjectMember[]>(`${this.baseUrl}/projects/${projectId}/members`, payload);
  }

  removeMember(tenantId: number, projectId: number, memberId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.delete(`${this.baseUrl}/projects/${projectId}/members/${memberId}`, { params });
  }

  getTapVersions(tenantId: number, projectId: number): Observable<ControlProjectTapVersion[]> {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<ControlProjectTapVersion[]>(`${this.baseUrl}/projects/${projectId}/tap-versions`, { params });
  }

  createTapVersion(projectId: number, payload: any): Observable<ControlProjectTapVersion> {
    return this.http.post<ControlProjectTapVersion>(`${this.baseUrl}/projects/${projectId}/tap-versions`, payload);
  }

  updateTapVersion(projectId: number, tapId: number, payload: any): Observable<ControlProjectTapVersion> {
    return this.http.put<ControlProjectTapVersion>(`${this.baseUrl}/projects/${projectId}/tap-versions/${tapId}`, payload);
  }

  getMilestones(tenantId: number, projectId: number): Observable<ControlProjectMilestone[]> {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<ControlProjectMilestone[]>(`${this.baseUrl}/projects/${projectId}/milestones`, { params });
  }

  createMilestone(projectId: number, payload: any): Observable<ControlProjectMilestone> {
    return this.http.post<ControlProjectMilestone>(`${this.baseUrl}/projects/${projectId}/milestones`, payload);
  }

  updateMilestone(projectId: number, milestoneId: number, payload: any): Observable<ControlProjectMilestone> {
    return this.http.put<ControlProjectMilestone>(`${this.baseUrl}/projects/${projectId}/milestones/${milestoneId}`, payload);
  }

  deleteMilestone(tenantId: number, projectId: number, milestoneId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.delete(`${this.baseUrl}/projects/${projectId}/milestones/${milestoneId}`, { params });
  }

  getHistory(tenantId: number, projectId: number): Observable<any[]> {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<any[]>(`${this.baseUrl}/projects/${projectId}/history`, { params });
  }

  getAttachments(tenantId: number, projectId: number): Observable<ControlProjectAttachment[]> {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<ControlProjectAttachment[]>(`${this.baseUrl}/projects/${projectId}/attachments`, { params });
  }

  uploadAttachment(projectId: number, formData: FormData): Observable<ControlProjectAttachment> {
    return this.http.post<ControlProjectAttachment>(`${this.baseUrl}/projects/${projectId}/attachments`, formData);
  }

  deleteAttachment(tenantId: number, projectId: number, attachmentId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.delete(`${this.baseUrl}/projects/${projectId}/attachments/${attachmentId}`, { params });
  }
}
